import React, { useState } from "react";
import LiveBotTerminal from "@/components/LiveBotTerminal";
import { ChartLineUp, ArrowsLeftRight, CaretDown } from "@phosphor-icons/react";
import { useGlobalData } from "@/lib/dataStreams";

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-800 group bg-slate-900/20 hover:bg-slate-900/40 transition-colors px-6 rounded-2xl mb-4">
      <button 
        onClick={() => setOpen(!open)} 
        className="w-full py-6 flex justify-between items-center text-left"
      >
        <span className="font-bold text-lg text-slate-200 group-hover:text-rose-400 transition-colors pr-4">{q}</span>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-colors ${open ? 'bg-rose-500/20 text-rose-400' : 'bg-slate-800 text-slate-400'}`}>
          <CaretDown size={16} className={`transform transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${open ? 'max-h-96 opacity-100 pb-6' : 'max-h-0 opacity-0'}`}>
        <p className="text-slate-300 leading-relaxed text-sm sm:text-base pr-8">
          {a}
        </p>
      </div>
    </div>
  );
};

function StrategyOverview() {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-sm">
      <ChartLineUp size={28} className="text-rose-400 mb-5" />
      <h3 className="text-xl font-bold mb-3 text-slate-100">Strategy Overview</h3>
      <ul className="text-slate-400 text-sm space-y-4 list-none mt-5">
        <li className="flex items-start gap-3">
          <div className="w-1.5 h-1.5 rounded-full bg-rose-400 mt-1.5 shrink-0" />
          Monitors large wallet (whale) accumulations in real-time.
        </li>
        <li className="flex items-start gap-3">
          <div className="w-1.5 h-1.5 rounded-full bg-rose-400 mt-1.5 shrink-0" />
          Calculates RSI and MACD natively using streaming tick data.
        </li>
        <li className="flex items-start gap-3">
          <div className="w-1.5 h-1.5 rounded-full bg-rose-400 mt-1.5 shrink-0" />
          Routes all swaps through Jupiter V6 API to guarantee best execution routing.
        </li>
        <li className="flex items-start gap-3">
          <div className="w-1.5 h-1.5 rounded-full bg-rose-400 mt-1.5 shrink-0" />
          Dynamic slippage tolerance based on network congestion.
        </li>
      </ul>
    </div>
  );
}

function AIParameters() {
  return (
    <div className="bg-rose-500/5 border border-rose-500/20 rounded-2xl p-8 shadow-sm relative overflow-hidden">
      <div className="absolute top-0 right-0 w-32 h-32 bg-rose-500/10 rounded-full blur-2xl pointer-events-none" />
      <h3 className="font-bold text-xs text-rose-400 uppercase tracking-wider mb-6 relative z-10">Current AI Parameters</h3>
      <div className="space-y-4 font-mono text-sm relative z-10">
        <div className="flex justify-between border-b border-rose-500/10 pb-3">
          <span className="text-slate-400 font-sans font-medium">Mode:</span>
          <span className="text-slate-200">Trend Following</span>
        </div>
        <div className="flex justify-between border-b border-rose-500/10 pb-3">
          <span className="text-slate-400 font-sans font-medium">Confidence Threshold:</span>
          <span className="text-emerald-400 font-bold">84.5%</span>
        </div>
        <div className="flex justify-between border-b border-rose-500/10 pb-3">
          <span className="text-slate-400 font-sans font-medium">Risk Allocation:</span>
          <span className="text-slate-200">Medium</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-400 font-sans font-medium">Target Asset:</span>
          <span className="text-indigo-400 font-bold">Wrapped SOL</span>
        </div>
      </div>
    </div>
  );
}

function JupiterLogs({ swaps }) {
  return (
    <div className="mb-12">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-sm uppercase tracking-wider text-slate-400 flex items-center gap-2">
          <ArrowsLeftRight size={18} className="text-rose-400" /> Jupiter V6 Aggregator Logs
        </h3>
        <div className="flex items-center gap-2 bg-rose-500/10 px-3 py-1.5 rounded-full border border-rose-500/20">
          <span className="w-2 h-2 bg-rose-400 rounded-full animate-pulse shadow-[0_0_8px_rgba(244,63,94,0.8)]" />
          <span className="text-rose-400 text-[10px] font-bold tracking-wider uppercase">Streaming AI Executions</span>
        </div>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm font-sans text-left whitespace-nowrap">
            <thead>
              <tr className="border-b border-slate-800 bg-slate-950/50 text-slate-400 text-xs uppercase tracking-wider font-bold">
                <th className="p-5">Time (UTC)</th>
                <th className="p-5">Execution Route</th>
                <th className="p-5">Sold Asset</th>
                <th className="p-5">Bought Asset</th>
                <th className="p-5 text-right">Profit</th>
                <th className="p-5 text-right">Mainnet Signature</th>
              </tr>
            </thead>
            <tbody className="font-mono text-sm">
              {!swaps || swaps.length === 0 ? (
                <tr><td colSpan="6" className="p-8 text-center text-slate-500 text-xs animate-pulse font-sans">Scanning Jupiter Router...</td></tr>
              ) : (
                swaps.map((s) => {
                  const time = new Date(s.ts).toLocaleTimeString([], { hour12: false, timeZone: 'UTC' });
                  return (
                    <tr key={s.signature} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
                      <td className="p-5 text-slate-400 text-[11px] tracking-wider">{time}</td>
                      <td className="p-5">
                        <div className="flex items-center gap-2 text-xs font-bold font-sans">
                          <span className={s.isBuy ? "text-emerald-400" : "text-slate-400"}>
                            {s.isBuy ? s.stableToken : "SOL"}
                          </span>
                          <span className="text-slate-600">→</span>
                          <span className={s.isBuy ? "text-slate-400" : "text-rose-400"}>
                            {s.isBuy ? "SOL" : s.stableToken}
                          </span>
                          <span className="ml-3 px-2 py-0.5 rounded bg-indigo-500/10 border border-indigo-500/20 text-[9px] text-indigo-400 uppercase tracking-widest">JUPITER</span>
                        </div>
                      </td>
                      <td className="p-5 font-bold text-slate-200">
                        {s.isBuy 
                          ? `${s.stableAmount.toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})} ${s.stableToken}`
                          : `${s.solAmount.toFixed(4)} SOL`
                        }
                      </td>
                      <td className="p-5 font-bold text-slate-200">
                        {s.isBuy 
                          ? `${s.solAmount.toFixed(4)} SOL`
                          : `${s.stableAmount.toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})} ${s.stableToken}`
                        }
                      </td>
                      <td className="p-5 text-right font-bold">
                        {s.pnl === null || s.pnl === undefined ? (
                          <span className="text-slate-500 animate-pulse">…</span>
                        ) : (
                          <span className={s.pnl >= 0 ? "text-emerald-400" : "text-rose-400"}>
                            {s.pnl >= 0 ? "+" : "−"}${Math.abs(s.pnl).toFixed(2)}
                          </span>
                        )}
                      </td>
                      <td className="p-5 text-right text-slate-500 text-xs truncate max-w-[150px]">
                        <a href={`https://solscan.io/tx/${s.signature}`} target="_blank" rel="noreferrer" className="hover:text-rose-400 transition-colors underline decoration-slate-700 underline-offset-4">
                          {s.signature}
                        </a>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function AITradeBot() {
  const { aiSwaps } = useGlobalData();
  const swaps = aiSwaps.slice(0, 40);

  const faqs = [
    {
      q: "How does the AI analyze order flow without lag?",
      a: "Rather than relying on 1-minute candlestick charts from CEXs, the AI connects directly to Solana RPC nodes to parse live mempool data and recently finalized blocks. This allows it to identify institutional accumulation (whale wallets) and pending volume blocks before they are fully priced into the market."
    },
    {
      q: "Why exclusively route through Jupiter V6?",
      a: "Jupiter is Solana's premier liquidity aggregator. By routing all swing trades through Jupiter V6, the bot ensures it achieves the best possible entry price by splitting the order across multiple DEXs (like Orca, Raydium, and Meteora) simultaneously, drastically reducing price impact on large sizes."
    },
    {
      q: "What indicators are used natively by the model?",
      a: "The system calculates localized RSI, MACD, and Bollinger Bands natively using real-time sub-second tick data. Because this data is processed locally rather than fetched via third-party APIs, the model reacts to momentum shifts milliseconds before retail charting platforms update."
    },
    {
      q: "Can I adjust the AI risk allocation?",
      a: "In the production version, operators can adjust confidence thresholds (e.g., 80% vs 95%) to balance trade frequency with risk. A higher threshold forces the AI to wait for near-perfect setups, while a lower threshold executes trades more aggressively during sideways chop."
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-sans selection:bg-rose-500/30 pb-20">
      <div className="max-w-[1500px] mx-auto px-4 md:px-8 py-12">
        <div className="mb-12 border-b border-slate-800 pb-8">
          <span className="text-rose-400 font-bold text-xs uppercase tracking-wider">// Module 03</span>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mt-3 text-slate-100">
            AI Momentum
          </h1>
          <p className="text-slate-300 mt-5 max-w-3xl text-base leading-relaxed">
            This AI-driven module focuses strictly on high-liquidity SOL ↔ USDC/USDT pairs. By analyzing on-chain order flow, mempool data, and macroscopic momentum indicators, the bot predicts short-term price vectors and executes highly optimized swing trades using the Jupiter aggregator.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          <div className="space-y-6 flex flex-col justify-between">
            <StrategyOverview />
            <AIParameters />
          </div>
          <div className="h-full min-h-[450px]">
            <LiveBotTerminal maxItems={30} />
          </div>
        </div>

        <JupiterLogs swaps={swaps} />

        {/* FAQ SECTION */}
        <div className="mt-20 max-w-[900px] mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-extrabold text-slate-100 mb-2">Momentum Knowledge Base</h2>
            <p className="text-slate-300">Technical insights into the momentum engine.</p>
          </div>
          <div className="space-y-2">
            {faqs.map((faq, i) => (
              <FaqItem key={i} q={faq.q} a={faq.a} />
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}
